import React, { useContext, useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import { FiEye, FiPlus, FiTrash } from 'react-icons/fi'
import classNames from 'classnames'

import ActionFooter from '../../../../components/ActionFooter'
import { AssociacaoContext } from '../../../../contexts/AssociacaoContext'
import formUtils from '../../../../util/form'
import { Button, File, Select, Textbox } from '../../../../components/Form'
import { api } from '../../../../services/api'
import Yup from '../../../../services/yup'
import Table from '../../../../components/Table'
import Modal from '../../../../components/Modal'
import { extname } from '../../../../util/path'
import { forceDownloadFileFromURL } from '../../../../util/file'

const initialValues = {
    grau: null,
    instituicao: null,
    certificado: null,
    nova_instituicao_uf_cidade: '',
    nova_instituicao_nome: ''
}

const validation = Yup.object({
    grau: Yup.object({ label: Yup.string(), value: Yup.mixed() }).required('Selecione o grau de formação.'),
    instituicao: Yup.object({ label: Yup.string(), value: Yup.mixed() }).required('Selecione a instituição.')
})

export function Formacao() {
    const { tempData, syncTempData, associar } = useContext(AssociacaoContext)

    const [formacoes, setFormacoes] = useState([])
    const [graus, setGraus] = useState([])
    const [faculdades, setFaculdades] = useState([])
    const [formacoesSelecionadas, setFormacoesSelecionadas] = useState([])
    const [formacoesIncluidas, setFormacoesIncluidas] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [openedFile, setOpenedFile] = useState(null)
    const [mostrarCadastroNovaInstituicao, setMostrarCadastroNovaInstituicao] = useState(false)
    const [mostrarValidacaoFormacaoSelecionadas, setMostrarValidacaoFormacoesSelecionadas] = useState(false)
    const [mostrarValidacaoInstituicaoNomeSelecionadas, setMostrarValidacaoInstituicaoNomeSelecionadas] = useState(false)
    const [mostrarValidacaoInstituicaoLocalSelecionadas, setMostrarValidacaoInstituicaoLocalSelecionadas] = useState(false)

    function handleAddFormacao(values, { setFieldValue }) {
        if (!formacoesSelecionadas.length) {
            setMostrarValidacaoFormacoesSelecionadas(true)
            return
        }

        if (values.instituicao.value === 9999) {
            if (!values.nova_instituicao_nome && !values.nova_instituicao_uf_cidade) {
                setMostrarValidacaoInstituicaoNomeSelecionadas(true)
                setMostrarValidacaoInstituicaoLocalSelecionadas(true)
                return
            } else if (!values.nova_instituicao_nome) {
                setMostrarValidacaoInstituicaoNomeSelecionadas(true)
                setMostrarValidacaoInstituicaoLocalSelecionadas(false)
                return
            } else if (!values.nova_instituicao_uf_cidade) {
                setMostrarValidacaoInstituicaoNomeSelecionadas(false)
                setMostrarValidacaoInstituicaoLocalSelecionadas(true)
                return
            }
        }

        setMostrarValidacaoInstituicaoNomeSelecionadas(false)
        setMostrarValidacaoInstituicaoLocalSelecionadas(false)
        setMostrarValidacaoFormacoesSelecionadas(false)

        setFormacoesIncluidas(old => {
            const formacaoTexto = formacoesSelecionadas.map(item => item.label).join(' - ')

            return [
                ...old,
                {
                    id: formacaoTexto,
                    grau: values.grau.label,
                    instituicao: values.instituicao.label,
                    instituicao_local: values.nova_instituicao_uf_cidade,
                    instituicao_nome: values.nova_instituicao_nome,
                    formacoes: formacaoTexto,
                    certificado: values.certificado
                }
            ]
        })

        const data = formacoesSelecionadas.map(item => {
            return formUtils.extractFormValues(item)
        })

        syncTempData({
            ...tempData,
            formacoes: [
                ...tempData?.formacoes || [],
                {
                    niveis: data,
                    grau: values.grau,
                    instituicao: values.instituicao,
                    instituicao_nome: values.nova_instituicao_nome || '',
                    instituicao_local: values.nova_instituicao_uf_cidade || '',
                    certificado: values.certificado
                }
            ]
        })

        setFormacoesSelecionadas([])

        setFieldValue('grau', null)
        setFieldValue('instituicao', null)
        setFieldValue('certificado', null)
        setFieldValue('nova_instituicao_nome', '')
        setFieldValue('nova_instituicao_uf_cidade', '')
        setMostrarCadastroNovaInstituicao(false)
    }

    async function finish() {
        try {
            setIsSubmitting(true)

            await associar(tempData)
        } catch (e) {
            toast.error(e.msg)
        } finally {
            setIsSubmitting(false)
        }
    }

    async function loadGraus() {
        const response = await api.get('grau_formacao')

        setGraus(response)
    }

    async function loadFormacoes(paiId = null) {
        try {
            const data = await api.get('formacao', {
                params: {
                    pai_id: paiId || null
                }
            })

            setFormacoes(data)
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function loadFaculdades() {
        const faculdades = await api.get('faculdade')

        setFaculdades(faculdades)
    }

    function handleRemoverFormacao(id) {
        const posicao = formacoesSelecionadas.findIndex(i => i.value === id)

        setFormacoesSelecionadas(old => old.slice(0, posicao))
    }

    function handleRemoverFormacaoIncluida(item) {
        const formacoesIncluidasAtualizado = formacoesIncluidas.filter(i => i.id !== item.id)

        setFormacoesIncluidas(formacoesIncluidasAtualizado)

        syncTempData({
            ...tempData,
            formacoes: tempData.formacoes.filter((i, index) => index !== item.id)
        })
    }

    async function handleOpenCertificado(arquivoId) {
        if (!arquivoId) {
            return
        }

        const data = await api.get(`arquivo/${arquivoId}`)

        if (extname(data.link) === '.pdf') {
            forceDownloadFileFromURL(data.link)
        } else {
            setOpenedFile(data.link)
        }
    }

    async function onSelectInstituicao(instituicaoId, setFieldValue) {
        setMostrarCadastroNovaInstituicao(instituicaoId === 9999)

        if (instituicaoId !== 9999) {
            setFieldValue('nova_instituicao_nome', '')
            setFieldValue('nova_instituicao_uf_cidade', '')
        }
    }

    useEffect(() => {
        if (!graus.length) {
            return
        }

        const { formacoes } = tempData

        if (formacoes) {
            setFormacoesIncluidas(formacoes.map((item, idx) => ({
                id: idx,
                grau: item.grau.label,
                instituicao: item.instituicao_nome || item.instituicao.label,
                formacoes: item.niveis.map(i => i.label).join(' - '),
                certificado: item.certificado
            })))
        }
    }, [tempData, graus])

    useEffect(() => {
        loadGraus()

        loadFaculdades()

        loadFormacoes()
    }, [])

    useEffect(() => {
        if (formacoesSelecionadas) {
            const ultimaFormacaoId = formacoesSelecionadas.at(-1)

            loadFormacoes(ultimaFormacaoId?.value || null)
        }

        setTimeout(() => {
            document.querySelector('.formacao-field > div > div > div:first-child').innerHTML = ''
        }, 100)
    }, [formacoesSelecionadas])

    if (!graus || !formacoes || !faculdades) {
        return null
    }

    return (
        <div>
            <Formik
                onSubmit={handleAddFormacao}
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validation}
            >
                {({ setFieldValue, errors }) => (
                    <Form className="flex flex-col pb-4">
                        <div>
                            <div className="grid grid-cols-[1fr_1.6fr] border-b border-gray-500 pb-4 sm:pb-0 sm:border-b-0 w-full">
                                <Select
                                    name="grau"
                                    label="Grau de formação"
                                    onChange={(selected, meta) => { setFieldValue(meta.name, selected) }}
                                    options={graus.map(formacao => ({
                                        label: formacao.descricao,
                                        value: formacao.id
                                    }))}
                                    isSearchable={false}
                                />

                                <Select
                                    name="instituicao"
                                    label="Instituição de ensino"
                                    onChange={(selected, meta) => {
                                        setFieldValue(meta.name, selected)

                                        onSelectInstituicao(selected.value, setFieldValue)
                                    }}
                                    options={faculdades.map(faculdade => ({
                                        label: `${faculdade.descricao} ${faculdade.sigla ? `(${faculdade.sigla})` : ''}`,
                                        value: faculdade.id
                                    }))}
                                />

                                {mostrarCadastroNovaInstituicao && (
                                    <>
                                        <div />

                                        <div className="flex flex-col lg:flex-row" >
                                            <div
                                                className={classNames('ml-2 pr-0', {
                                                    'border-l-4 border-[#ffeb3b] pl-0': mostrarValidacaoInstituicaoNomeSelecionadas
                                                })}
                                            >
                                                <Textbox
                                                    label="Nome da instituição"
                                                    name="nova_instituicao_nome"
                                                />
                                                {mostrarValidacaoInstituicaoNomeSelecionadas && (
                                                    <div className="bg-[#ffeb3b] text-slate-800 rounded-md p-1 text-xs ml-2 mr-2">
                                                        Informe o nome da Instituição.
                                                    </div>
                                                )}
                                            </div>

                                            <div
                                                className={classNames('ml-2 pr-0', {
                                                    'border-l-4 border-[#ffeb3b] pl-0': mostrarValidacaoInstituicaoLocalSelecionadas
                                                })}
                                            >
                                                <Textbox
                                                    label="Estado e cidade da instituição"
                                                    name="nova_instituicao_uf_cidade"
                                                />
                                                {mostrarValidacaoInstituicaoLocalSelecionadas && (
                                                    <div className="bg-[#ffeb3b] text-slate-800 rounded-md p-1 text-xs ml-2 mr-2">
                                                        Informe a cidade e estado da instituição.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                <File
                                    name="certificado"
                                    label="Anexe o Certificado de Conclusão"
                                    onSuccess={fileId => setFieldValue('certificado', fileId)}
                                    format="square"
                                    error={errors.certificado}
                                    style={{ width: 'calc(100% - 16px)' }}
                                    previewSize={['100%', '140px']}
                                />

                                <div className="flex flex-col">
                                    <div
                                        className={classNames('ml-2 pr-0', {
                                            'border-l-4 border-[#ffeb3b] pl-2': mostrarValidacaoFormacaoSelecionadas
                                        })}
                                    >
                                        <Select
                                            name="formacao"
                                            label="Formação"
                                            withoutForm
                                            className="formacao-field"
                                            containerStyle={{
                                                marginLeft: 0,
                                                width: 'calc(100% - 8px)'
                                            }}
                                            onChange={selected => {
                                                setFormacoesSelecionadas(old => [...old, selected])

                                                setMostrarValidacaoFormacoesSelecionadas(false)
                                            }}
                                            options={formacoes.map(formacao => ({
                                                label: formacao.descricao,
                                                value: formacao.id
                                            }))}
                                        />

                                        {mostrarValidacaoFormacaoSelecionadas && (
                                            <div className="bg-[#ffeb3b] text-slate-800 rounded-md p-1 text-xs mr-2">
                                                Selecione pelo menos uma formação.
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col">
                                        {!!formacoesSelecionadas.length && !!formacoes.length && (
                                            <div className="text-xs text-gray-300 w-full m-2">
                                                Você pode ser mais específico em sua formação, selecionando acima.
                                            </div>
                                        )}

                                        <div className="flex gap-2 w-full px-2 py-3 col-start-1 items-center">
                                            {formacoesSelecionadas.map(formacaoSelecionada => (
                                                <div key={formacaoSelecionada.value} className="flex space-between gap-2 border rounded-md border-white p-2 items-center">
                                                    <span>{formacaoSelecionada.label}</span>

                                                    <FiTrash
                                                        size={16}
                                                        onClick={() => {
                                                            handleRemoverFormacao(formacaoSelecionada.value)
                                                        }}
                                                        className="cursor-pointer hover:text-red-500"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    className="transparent w-fit justify-self-end col-start-2"
                                    type="submit"
                                >
                                    Incluir esta formação
                                    <FiPlus size={16} />
                                </Button>

                                <div className="w-full px-2 col-span-2">
                                    <Table
                                        headers={[
                                            { name: 'grau', value: 'Grau', centered: true },
                                            { name: 'formacoes', value: 'Formação', centered: true },
                                            { name: 'instituicao', value: 'Instituição', centered: true }
                                        ]}
                                        data={formacoesIncluidas}
                                        actions={[
                                            {
                                                action: item => handleOpenCertificado(item.certificado),
                                                name: 'Abrir',
                                                icon: FiEye,
                                                iconSize: 16,
                                                checkDisabled: item => !item.certificado
                                            },
                                            {
                                                action: handleRemoverFormacaoIncluida,
                                                name: 'Remover',
                                                icon: FiTrash,
                                                iconSize: 16
                                            }
                                        ]}
                                        emptyLabel="Nenhuma formação incluída"
                                    />
                                </div>

                                {openedFile && <p>{openedFile}</p>}
                            </div>
                        </div>

                        <ActionFooter>
                            <Button
                                className="white w-fit self-end"
                                onClick={finish}
                                loading={isSubmitting}
                            >
                                Finalizar
                            </Button>
                        </ActionFooter>
                    </Form>
                )}
            </Formik>

            <Modal
                isOpen={!!openedFile}
                handleClose={() => {
                    setOpenedFile(null)
                }}
                title="Visualização"
            >
                <img src={openedFile} alt="" className="w-full" />
            </Modal>
        </div>
    )
} 
